// Feature Documentation: docs/features/refactor-strict-analysis-and-carrier-data.md
import { useState, useEffect, useRef, useContext } from 'react';
import { callApi } from '../../callApi';
import { AppContext } from '../AppContext';
import { useFeatureFlags } from '../FeatureFlags';

export const useStrictAnalysisData = (caseId) => {
  const { accessToken } = useContext(AppContext);
  const { featureFlags } = useFeatureFlags();
  
  const orgName = featureFlags?.menu_version?.toLowerCase() || '';
  const isNMA = orgName === 'nma' || orgName.includes('revedy');
  const isUSN = orgName === 'us neuro' || orgName === 'usneuro';
  const isMPH = orgName.includes('mpower') || orgName === 'mph';
  const isAdmin = !isNMA && !isUSN && !isMPH;

  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Core Data
  const [caseData, setCaseData] = useState(null);
  const [carrierData, setCarrierData] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  
  // Analysis Results
  const [results, setResults] = useState(null);
  const [payerResponse, setPayerResponse] = useState(null);
  const [claimsData, setClaimsData] = useState(null);
  
  // Metadata
  const [techMetadata, setTechMetadata] = useState(null);
  const [isTechMetadataLoading, setIsTechMetadataLoading] = useState(false);
  
  // UI State
  const [claimType, setClaimType] = useState('pro');
  const [pipelineVersion, setPipelineVersion] = useState('advanced');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const skipFetchRef = useRef(false);

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const fetchExistingResults = async () => {
      if (skipFetchRef.current) {
        skipFetchRef.current = false;
        return;
      }
      
      if (!caseId || !accessToken) return;
      
      setInitialLoading(true);
      setError(null);
      
      try {
        // 1. Fetch Case Details (includes files and carrier data)
        const detailsResponse = await callApi(`/get_case_details/${caseId}`, accessToken);
        if (detailsResponse.ok) {
          const detailsData = await detailsResponse.json();
          setCaseData(detailsData);
          setCaseFiles(detailsData.files || []);
          setCarrierData(detailsData.carrier_data || null);
          
          // Redirect if this is a Clinical (Surgical Assist) case
          if (detailsData.case_type === 'Clinical') {
            window.location.href = `/mph-surgical-assist/${caseId}`;
            return;
          }
        }

        // 2. Fetch Analysis Results
        let endpoint = `/nma/analyze_case/${caseId}?claim_type=${claimType}&pipeline_version=${pipelineVersion}`;
        if (isUSN) {
          endpoint = `/usn/analyze_case/${caseId}?claim_type=${claimType}`;
        } else if (isMPH) {
          endpoint = `/mph/analyze_case/${caseId}?claim_type=${claimType}&pipeline_version=${pipelineVersion}`;
        }

        const response = await callApi(endpoint, accessToken, { method: 'GET' });
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            setResults(data.results || null);
            setPayerResponse(data.payer_response || null);
            setClaimsData(data.claims || null);
          } else {
            setResults(null);
            setPayerResponse(null);
            setClaimsData(null);
          }
        } else if (response.status !== 404) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Error fetching existing results:", errorData);
        }

        // 3. Fetch Tech Report Metadata
        setIsTechMetadataLoading(true);
        try {
          let metaEndpoint = `/nma/tech_report_metadata/${caseId}`;
          if (isUSN) {
            metaEndpoint = `/usn/tech_report_metadata/${caseId}`;
          }
          
          const metaResponse = await callApi(metaEndpoint, accessToken, { method: 'GET' });
          if (metaResponse.ok) {
            const metaData = await metaResponse.json();
            if (metaData.metadata) {
              setTechMetadata(metaData.metadata);
            }
          }
        } catch (err) {
          console.error("Error fetching tech metadata:", err);
        } finally {
          setIsTechMetadataLoading(false);
        }
      } catch (err) {
        console.error('Failed to check for existing results:', err);
        setError(err.message);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchExistingResults();
  }, [caseId, claimType, pipelineVersion, accessToken, isUSN, isNMA, isMPH, isAdmin, refreshTrigger]);

  return {
    initialLoading,
    error,
    caseData,
    carrierData,
    caseFiles,
    results,
    setResults,
    payerResponse,
    setPayerResponse,
    claimsData,
    setClaimsData,
    techMetadata,
    setTechMetadata,
    isTechMetadataLoading,
    claimType,
    setClaimType,
    pipelineVersion,
    setPipelineVersion,
    skipFetchRef,
    isNMA,
    isUSN,
    isMPH,
    isAdmin,
    refreshData
  };
};
